import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import FontFamily from "@tiptap/extension-font-family";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import Heading from "@tiptap/extension-heading";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Type,
  Palette,
  Strikethrough,
  RotateCcw,
  RotateCw,
  Type as FontSizeIcon,
} from "lucide-react";

// Custom hook for debounced functions
function useDebounced(fn, delay = 2000) {
  const timeoutRef = useRef(null);
  return useCallback(
    (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay]
  );
}

// Font options for the editor
const FONT_OPTIONS = [
  { name: "Georgia", value: "Georgia, serif" },
  { name: "Times New Roman", value: "Times New Roman, serif" },
  { name: "Arial", value: "Arial, sans-serif" },
  { name: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { name: "Verdana", value: "Verdana, sans-serif" },
  { name: "Tahoma", value: "Tahoma, sans-serif" },
  { name: "Calibri", value: "Calibri, sans-serif" },
  { name: "Trebuchet MS", value: "Trebuchet MS, sans-serif" },
  { name: "Courier New", value: "Courier New, monospace" },
  { name: "Comic Sans MS", value: "Comic Sans MS, cursive" },
  { name: "Impact", value: "Impact, sans-serif" },
  { name: "Lato", value: "Lato, sans-serif" },
  { name: "Montserrat", value: "Montserrat, sans-serif" },
  { name: "Source Sans Pro", value: "Source Sans Pro, sans-serif" },
];

const FONT_SIZE_OPTIONS = [
  { name: "8px", value: "8px" },
  { name: "9px", value: "9px" },
  { name: "10px", value: "10px" },
  { name: "11px", value: "11px" },
  { name: "12px", value: "12px" },
  { name: "14px", value: "14px" },
  { name: "16px", value: "16px" },
  { name: "18px", value: "18px" },
  { name: "20px", value: "20px" },
  { name: "24px", value: "24px" },
  { name: "28px", value: "28px" },
  { name: "32px", value: "32px" },
  { name: "36px", value: "36px" },
  { name: "48px", value: "48px" },
];

const COLOR_OPTIONS = [
  { name: "Black", value: "#000000" },
  { name: "Dark Gray", value: "#374151" },
  { name: "Gray", value: "#6b7280" },
  { name: "Light Gray", value: "#9ca3af" },
  { name: "Red", value: "#ef4444" },
  { name: "Orange", value: "#f97316" },
  { name: "Yellow", value: "#eab308" },
  { name: "Green", value: "#22c55e" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Purple", value: "#a855f7" },
  { name: "Pink", value: "#ec4899" },
  { name: "Brown", value: "#a16207" },
];

// Toolbar component (same as ModernClientNotesModal)
function TripNotesToolbar({ editor }) {
  if (!editor) return null;

  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);

  const setFontFamily = (fontFamily) => {
    editor.chain().focus().setFontFamily(fontFamily).run();
    setShowFontDropdown(false);
  };

  const setFontSize = (fontSize) => {
    editor.chain().focus().setFontSize(fontSize).run();
    setShowSizeDropdown(false);
  };

  const setTextColor = (color) => {
    editor.chain().focus().setColor(color).run();
    setShowColorDropdown(false);
  };

  const setTextAlign = (alignment) => {
    editor.chain().focus().setTextAlign(alignment).run();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFontDropdown && !event.target.closest(".trip-notes-font-dropdown")) setShowFontDropdown(false);
      if (showSizeDropdown && !event.target.closest(".trip-notes-size-dropdown")) setShowSizeDropdown(false);
      if (showColorDropdown && !event.target.closest(".trip-notes-color-dropdown")) setShowColorDropdown(false);
    };
    if (showFontDropdown || showSizeDropdown || showColorDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showFontDropdown, showSizeDropdown, showColorDropdown]);

  const btnBox = "h-8 w-8";
  const pillBtn = "h-8 px-2.5";
  const selectBtn = "h-8 px-3";

  return (
    <div className="trip-notes-toolbar flex flex-wrap gap-1.5 items-center p-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
      <div className="flex gap-1 items-center">
        <button onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 hover:bg-gray-200`} title="Undo"><RotateCcw className="w-4 h-4" /></button>
        <button onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 hover:bg-gray-200`} title="Redo"><RotateCw className="w-4 h-4" /></button>
      </div>
      <div className="w-px h-6 bg-gray-300" />
      <div className="flex gap-1 items-center">
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("bold") ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Bold"><Bold className="w-4 h-4" /></button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("italic") ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Italic"><Italic className="w-4 h-4" /></button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("underline") ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Underline"><UnderlineIcon className="w-4 h-4" /></button>
        <button onClick={() => editor.chain().focus().toggleStrike().run()} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("strike") ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Strikethrough"><Strikethrough className="w-4 h-4" /></button>
      </div>
      <div className="w-px h-6 bg-gray-300" />
      <div className="flex gap-1 items-center">
        <button onClick={() => setTextAlign("left")} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive({ textAlign: "left" }) ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Align Left"><AlignLeft className="w-4 h-4" /></button>
        <button onClick={() => setTextAlign("center")} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive({ textAlign: "center" }) ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Align Center"><AlignCenter className="w-4 h-4" /></button>
        <button onClick={() => setTextAlign("right")} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive({ textAlign: "right" }) ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Align Right"><AlignRight className="w-4 h-4" /></button>
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("bulletList") ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Bullet List"><List className="w-4 h-4" /></button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`${btnBox} flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("orderedList") ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Numbered List"><ListOrdered className="w-4 h-4" /></button>
      </div>
      <div className="w-px h-6 bg-gray-300" />
      <div className="flex gap-1 items-center">
        <button onClick={() => editor.chain().focus().setParagraph().run()} className={`${pillBtn} rounded-md text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${!editor.isActive("heading") ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Normal">Normal</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`${pillBtn} rounded-md text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("heading", { level: 1 }) ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Heading 1">H1</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`${pillBtn} rounded-md text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("heading", { level: 2 }) ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Heading 2">H2</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`${pillBtn} rounded-md text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editor.isActive("heading", { level: 3 }) ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-200"}`} title="Heading 3">H3</button>
      </div>
      <div className="w-px h-6 bg-gray-300" />
      <div className="flex relative gap-2 items-center trip-notes-font-dropdown">
        <button onClick={() => setShowFontDropdown(!showFontDropdown)} className={`${selectBtn} text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 min-w-[160px]`}>
          <Type className="w-4 h-4 text-gray-600" />
          <span className="truncate">{FONT_OPTIONS.find((font) => font.value === (editor.getAttributes("textStyle").fontFamily || "Georgia, serif"))?.name || "Georgia"}</span>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {showFontDropdown && (
          <div className="overflow-y-auto absolute left-0 top-full z-[200] mt-1 w-56 max-h-60 bg-white rounded-lg border border-gray-300 shadow-xl">
            {FONT_OPTIONS.map((font) => (
              <button key={font.value} onClick={() => setFontFamily(font.value)} className="flex justify-between items-center px-3 py-2 w-full text-sm text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none" style={{ fontFamily: font.value }}>
                <span>{font.name}</span>
                {editor.getAttributes("textStyle").fontFamily === font.value && <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex relative gap-2 items-center trip-notes-size-dropdown">
        <button onClick={() => setShowSizeDropdown(!showSizeDropdown)} className={`${selectBtn} text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 min-w-[96px]`}>
          <FontSizeIcon className="w-4 h-4 text-gray-600" />
          <span className="truncate">{editor.getAttributes("textStyle").fontSize || "14px"}</span>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {showSizeDropdown && (
          <div className="overflow-y-auto absolute left-0 top-full z-[200] mt-1 w-40 max-h-60 bg-white rounded-lg border border-gray-300 shadow-xl">
            {FONT_SIZE_OPTIONS.map((size) => (
              <button key={size.value} onClick={() => setFontSize(size.value)} className="flex justify-between items-center px-3 py-2 w-full text-sm text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none" style={{ fontSize: size.value }}>
                <span>{size.name}</span>
                {editor.getAttributes("textStyle").fontSize === size.value && <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex relative gap-2 items-center trip-notes-color-dropdown">
        <button onClick={() => setShowColorDropdown(!showColorDropdown)} className={`${selectBtn} text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 min-w-[120px]`}>
          <Palette className="w-4 h-4 text-gray-600" />
          <span className="truncate">{COLOR_OPTIONS.find((color) => color.value === editor.getAttributes("textStyle").color)?.name || "Black"}</span>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {showColorDropdown && (
          <div className="overflow-y-auto absolute left-0 top-full z-[200] mt-1 w-56 max-h-60 bg-white rounded-lg border border-gray-300 shadow-xl">
            {COLOR_OPTIONS.map((color) => (
              <button key={color.value} onClick={() => setTextColor(color.value)} className="flex justify-between items-center px-3 py-2 w-full text-sm text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none" style={{ color: color.value }}>
                <div className="flex gap-2 items-center">
                  <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: color.value }}></div>
                  <span>{color.name}</span>
                </div>
                {editor.getAttributes("textStyle").color === color.value && <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Normalize malformed HTML helper (same as ModernClientNotesModal)
function normalizeMalformedHtml(raw) {
  if (!raw || typeof raw !== "string") return "";
  let str = raw.trim();
  if (/[<>]/.test(str)) return str;
  str = str.replace(/\/?pp/gi, "</p><p>").replace(/\/?p(\s|$)/gi, "</p>$1").replace(/\bp</gi, "<p>").replace(/\/?span/gi, "</span>").replace(/\/?strong/gi, "</strong>").replace(/\/?em/gi, "</em>").replace(/\bbr\b/gi, "<br>");
  if (!/[<>]/.test(str)) str = `<p>${str}</p>`;
  if (!str.startsWith("<")) str = `<p>${str}`;
  if (!str.endsWith(">") || /[^>]$/.test(str)) str = `${str}</p>`;
  return str.replace(/(<\/p>\s*){2,}/g, "</p>").replace(/(<p>\s*){2,}/g, "<p>");
}

// Main modal - Trip-specific, uses separate TripNotes API (not ClientNotes)
export default function TripNotesEditorModal({ tripId, tripName, isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, bulletList: false, orderedList: false, underline: false, strike: false, code: false, blockquote: false }),
      FontFamily.configure({ types: ["textStyle"] }),
      TextStyle,
      Underline,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Heading.configure({ levels: [1, 2, 3] }),
      BulletList,
      OrderedList,
    ],
    content: "",
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      setWordCount(text.split(/\s+/).filter((word) => word.length > 0).length);
      setHasUnsavedChanges(true);
    },
    editorProps: {
      attributes: { class: "prose prose-sm max-w-none focus:outline-none min-h-[400px]", style: "font-family: Georgia, serif; line-height: 1.6; font-size: 14px;" },
    },
  });

  const debouncedSave = useDebounced(async () => {
    if (!editor || !hasUnsavedChanges || !tripId) return;
    try {
      const content = editor.getHTML();
      const text = editor.getText();
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/trips/${tripId}/notes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content, text, ink: [], font: "Georgia, serif" }),
      });
      if (response.ok) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
      } else if (response.status !== 429) {
        console.error("Auto-save failed with status:", response.status);
      }
    } catch (error) {
      console.error("Auto-save error:", error);
    }
  }, 2000);

  useEffect(() => {
    if (!isOpen || !tripId || !editor) return;
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/trips/${tripId}/notes`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (!ignore) {
            const content = data?.data?.content || "";
            if (content && content.trim()) {
              let fixedContent = content;
              if (!fixedContent.includes("<") && !fixedContent.includes(">")) fixedContent = normalizeMalformedHtml(fixedContent);
              fixedContent = fixedContent.replace(/^p([^<])/g, "<p>$1").replace(/([^>])p$/g, "$1</p>").replace(/\bbr\b/g, "<br>");
              try {
                const tempElement = document.createElement("div");
                tempElement.innerHTML = fixedContent;
                const properlyParsedHTML = tempElement.innerHTML;
                editor.commands.setContent(properlyParsedHTML, false);
                if (properlyParsedHTML !== content) {
                  const healedText = editor.getText();
                  fetch(`${import.meta.env.VITE_API_URL}/api/trips/${tripId}/notes`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ content: properlyParsedHTML, text: healedText, ink: [], font: "Georgia, serif" }),
                  }).then(() => setLastSaved(new Date())).catch(() => {});
                }
              } catch {
                editor.commands.setContent(fixedContent, false);
              }
            } else {
              editor.commands.clearContent(false);
            }
            setHasUnsavedChanges(false);
          }
        }
      } catch (error) {
        console.error("Error loading trip notes:", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [isOpen, tripId, editor]);

  useEffect(() => {
    if (hasUnsavedChanges) debouncedSave();
  }, [hasUnsavedChanges, debouncedSave]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .trip-notes-editor .ProseMirror { outline: none; white-space: pre-wrap; font-family: Georgia, serif; line-height: 1.6; font-size: 14px; color: #374151; }
        .trip-notes-editor .ProseMirror p { margin: 0 0 1em 0; display: block; }
        .trip-notes-editor .ProseMirror p:last-child { margin-bottom: 0; }
        .trip-notes-editor .ProseMirror strong { font-weight: bold; }
        .trip-notes-editor .ProseMirror em { font-style: italic; }
        .trip-notes-editor .ProseMirror u { text-decoration: underline; }
        .trip-notes-editor .ProseMirror s { text-decoration: line-through; }
        .trip-notes-editor .ProseMirror code { background-color: #f3f4f6; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: 'Courier New', monospace; font-size: 0.875em; }
        .trip-notes-editor .ProseMirror blockquote { border-left: 4px solid #e5e7eb; padding-left: 1rem; margin: 1rem 0; font-style: italic; color: #6b7280; }
        .trip-notes-editor .ProseMirror h1, .trip-notes-editor .ProseMirror h2, .trip-notes-editor .ProseMirror h3 { margin: 1em 0 0.5em 0; font-weight: bold; display: block; }
        .trip-notes-editor .ProseMirror h1 { font-size: 1.5em; }
        .trip-notes-editor .ProseMirror h2 { font-size: 1.3em; }
        .trip-notes-editor .ProseMirror h3 { font-size: 1.1em; }
        .trip-notes-editor .ProseMirror ul, .trip-notes-editor .ProseMirror ol { padding-left: 1.5em; margin: 1em 0; }
        .trip-notes-editor .ProseMirror ul { list-style-type: disc; }
        .trip-notes-editor .ProseMirror ol { list-style-type: decimal; }
        .trip-notes-editor .ProseMirror li { margin: 0.25em 0; display: list-item; color: #1f2937; }
      ` }} />
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50"
        role="dialog"
        aria-modal="true"
        aria-label="Trip Notes"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="relative w-full max-w-4xl h-full max-h-[90vh] rounded-2xl shadow-2xl bg-white overflow-hidden flex flex-col trip-notes-editor">
          <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-gray-200">
            <p className="mb-3 text-xs text-gray-500">Your trip notes are automatically saved as you type.</p>
            <div className="flex justify-between items-center mb-3">
              <div className="flex gap-3 items-center">
                <h2 className="text-xl font-semibold text-gray-900">Trip Notes{tripName ? ` – ${tripName}` : ""}</h2>
                {loading && <span className="text-xs text-gray-500">Loading…</span>}
                {lastSaved && <span className="text-xs text-green-600">Saved {lastSaved.toLocaleTimeString()}</span>}
                {hasUnsavedChanges && <span className="text-xs text-orange-600">Unsaved changes</span>}
              </div>
              <div className="flex gap-3 items-center">
                <div className="text-xs text-gray-500">{wordCount} words</div>
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">Close</button>
              </div>
            </div>
            <TripNotesToolbar editor={editor} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto bg-gradient-to-br from-gray-100 to-gray-200">
            <div className="relative p-6 h-full">
              <div className="relative w-full h-full bg-white rounded-lg border border-gray-300 shadow-lg" style={{ boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.1)" }}>
                <div className="absolute top-0 left-12 w-0.5 h-full bg-red-300/70"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-gray-200 to-transparent rounded-lg opacity-5"></div>
                <div className="overflow-y-auto relative h-full">
                  {editor && (
                    <EditorContent
                      editor={editor}
                      className="p-8 pl-16 max-w-none min-h-full focus:outline-none prose prose-sm ProseMirror"
                      style={{ fontFamily: "Georgia, serif", lineHeight: "1.6", fontSize: "14px", color: "#374151" }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Render via portal to document.body so modal appears on top of entire page
  return createPortal(modalContent, document.body);
}
